// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton) and setters (stable). See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, RefreshCw, UserX, Search, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  } catch (_e) { /* ignore */ }
  const el = document.createElement("textarea");
  el.value = text;
  document.body.appendChild(el);
  el.select();
  try { document.execCommand("copy"); } catch (_e) { /* ignore */ }
  document.body.removeChild(el);
  return Promise.resolve();
}

const ROLE_LABEL = {
  admin: "Admin", support: "Support", logistics: "Logistics",
  sales: "Sales", marketing: "Marketing",
  b2b_admin: "B2B Admin", b2b_agent: "B2B Agent",
};

const ROLE_COLOR = {
  admin: "bg-rose-500/15 text-rose-600 border-rose-500/30",
  support: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  logistics: "bg-blue-500/15 text-blue-600 border-blue-500/30",
  sales: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  marketing: "bg-purple-500/15 text-purple-600 border-purple-500/30",
  b2b_admin: "bg-teal-500/15 text-teal-600 border-teal-500/30",
  b2b_agent: "bg-slate-500/15 text-slate-600 border-slate-500/30",
};

export default function Employees() {
  const [rows, setRows] = useState([]);
  const [cfg, setCfg] = useState({ roles: [], departments: [] });
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "support", department: "" });
  const [saving, setSaving] = useState(false);
  const [invite, setInvite] = useState(null); // { name, email, invite_link, email_status, temp_password }

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (roleFilter !== "all") params.role = roleFilter;
    if (deptFilter !== "all") params.department = deptFilter;
    const r = await api.get("/crm/employees", { params });
    setRows(r.data);
  }, [q, roleFilter, deptFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/crm/employees/config").then(r => setCfg(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/crm/employees", form);
      setInvite(data);
      toast.success(`Employee added${data.email_status === "sent" ? " · invite emailed" : ""}`);
      setForm({ name: "", email: "", phone: "", role: "support", department: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add employee");
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async (emp) => {
    try {
      const { data } = await api.post(`/crm/employees/${emp.id}/resend-invite`);
      setInvite({ ...emp, ...data });
      toast.success(data.email_status === "sent" ? "Invite email resent" : "Invite link regenerated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend invite");
    }
  };

  const deactivate = async (emp) => {
    if (!window.confirm(`Deactivate ${emp.name}? They won't be able to log in.`)) return;
    try {
      await api.delete(`/crm/employees/${emp.id}`);
      toast.success("Employee deactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const copyLink = async (link) => {
    await copyToClipboard(link);
    toast.success("Invite link copied");
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Employees</h2>
        <Button onClick={() => setOpen(true)} data-testid="crm-new-employee-btn">
          <Plus className="w-4 h-4 mr-2" /> Add employee
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="pl-9"
            data-testid="emp-search"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-40" data-testid="emp-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {(cfg.roles || []).map(r => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-44" data-testid="emp-dept-filter"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {(cfg.departments || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Role</th>
                <th className="text-left px-4 py-2.5">Department</th>
                <th className="text-left px-4 py-2.5">Invite</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted-foreground py-8 text-sm">
                  No employees match those filters.
                </td></tr>
              )}
              {rows.map(e => (
                <tr key={e.id} className="border-t border-border" data-testid={`emp-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{e.name || "—"}</div>
                    {e.phone && <div className="text-xs text-muted-foreground">{e.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{e.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${ROLE_COLOR[e.role] || ROLE_COLOR.support}`}>
                      {ROLE_LABEL[e.role] || e.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">{e.department || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3">
                    {e.invite_status === "accepted" ? (
                      <Badge variant="secondary" className="text-[10px]">Accepted</Badge>
                    ) : e.invite_status === "pending" ? (
                      <Badge className="text-[10px] bg-amber-500/15 text-amber-600 border-amber-500/30">Pending</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => resendInvite(e)} data-testid={`emp-resend-${e.id}`} title="Resend invite">
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      {e.active !== false && (
                        <Button size="sm" variant="ghost" onClick={() => deactivate(e)} data-testid={`emp-deactivate-${e.id}`} title="Deactivate">
                          <UserX className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add employee</DialogTitle>
            <DialogDescription className="text-xs">
              They'll receive an email with a link to set their password. You'll also get a copy-paste link.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Full name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="emp-form-name" />
            </div>
            <div className="col-span-2"><Label className="text-xs">Work email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="emp-form-email" />
            </div>
            <div><Label className="text-xs">Phone</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
            </div>
            <div><Label className="text-xs">Role *</Label>
              <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                <SelectTrigger data-testid="emp-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(cfg.roles || []).map(r => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Department</Label>
              <Select value={form.department} onValueChange={v => setForm({...form, department: v})}>
                <SelectTrigger data-testid="emp-form-department"><SelectValue placeholder="Pick a department" /></SelectTrigger>
                <SelectContent>
                  {(cfg.departments || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground mt-1">Configure the list under Admin → Master Data → Department.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="emp-form-save">
              {saving ? "Adding…" : "Add employee & send invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite-result dialog */}
      <Dialog open={!!invite} onOpenChange={(o) => !o && setInvite(null)}>
        <DialogContent>
          {invite && (
            <>
              <DialogHeader>
                <DialogTitle>Invite ready · {invite.name || invite.email}</DialogTitle>
                <DialogDescription className="text-xs">
                  {invite.email_status === "sent"
                    ? "We emailed them the link below. You can also share it directly."
                    : "Email delivery failed or wasn't attempted — share the link below."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Invite link</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={invite.invite_link} readOnly data-testid="emp-invite-link" />
                    <Button variant="outline" size="sm" onClick={() => copyLink(invite.invite_link)} data-testid="emp-copy-invite">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {invite.temp_password && (
                  <div>
                    <Label className="text-xs">Temporary password (fallback if they can't open the link)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={invite.temp_password} readOnly data-testid="emp-temp-password" />
                      <Button variant="outline" size="sm" onClick={() => copyLink(invite.temp_password)}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" />
                  Email status: <span className="font-medium">{invite.email_status || "unknown"}</span>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setInvite(null)} data-testid="emp-invite-close">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
